import { useAnalyticsFeatureFlag } from '../contexts/AnalyticsFeatureFlagContext'
import { DataFormat } from '../data/model/shared'
import UserSession from '../utils/UserSession'
import { buildRequestUrl, createApiConfig } from '../utils/apiUtils'
import axios from '../utils/axiosWithRetry'

const RAW_DATA_CLOUD_FUNCTION_URL = `${process.env.REACT_APP_RAW_DATA_DOWNLOAD_URL}/fd-core`
const ARTIFACTS_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/artifacts/survey`

// Combined (multi-market) raw-data download is keyed on the project, not a
// survey: the backend pools respondent data across the project's market
// surveys. Core-responses-only (no legacy equivalent), like the combined
// results endpoint.
const ARTIFACTS_MULTIMARKET_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/artifacts/multimarket`

interface Data {
  getRawData: (surveyId: string, dataFormat: DataFormat) => Promise<string>
}

type SurveyDataOutputFormat = 'csv' | 'xlsx' | 'spss'

interface ArtifactsDownloadPayload {
  survey_data_output_format: SurveyDataOutputFormat
  layout: 'wide' | 'long'
  encoding_format: 'encoded' | 'unencoded'
  compression_format: 'zip'
  statuses: string[]
}

const getDimensionFromDataFormat: (dataFormat: DataFormat) => string = (
  dataFormat
) => {
  switch (dataFormat) {
    case DataFormat.WIDE_ENCODED:
    case DataFormat.WIDE_UNENCODED:
    case DataFormat.SPSS:
    case DataFormat.XLSX_UNENCODED:
      return 'WIDE'
    case DataFormat.LONG_ENCODED:
    case DataFormat.LONG_UNENCODED:
    default:
      return 'LONG'
  }
}

const getFormatFromDataFormat: (dataFormat: DataFormat) => string = (
  dataFormat
) => {
  switch (dataFormat) {
    case DataFormat.SPSS:
      return 'SPSS'
    case DataFormat.WIDE_ENCODED:
    case DataFormat.LONG_ENCODED:
      return 'CSV_ENCODED'
    case DataFormat.XLSX_UNENCODED:
      return 'XLSX_UNENCODED'
    case DataFormat.WIDE_UNENCODED:
    case DataFormat.LONG_UNENCODED:
    default:
      return 'CSV_UNENCODED'
  }
}

const mapDataFormatToArtifactsPayload: (
  dataFormat: DataFormat
) => ArtifactsDownloadPayload = (dataFormat) => {
  const dimension = getDimensionFromDataFormat(dataFormat)

  let survey_data_output_format: SurveyDataOutputFormat = 'csv'
  if (dataFormat === DataFormat.SPSS) {
    survey_data_output_format = 'spss'
  } else if (dataFormat === DataFormat.XLSX_UNENCODED) {
    survey_data_output_format = 'xlsx'
  }

  const encoding_format: 'encoded' | 'unencoded' =
    dataFormat === DataFormat.WIDE_ENCODED ||
    dataFormat === DataFormat.LONG_ENCODED ||
    dataFormat === DataFormat.SPSS
      ? 'encoded'
      : 'unencoded'

  return {
    survey_data_output_format,
    layout: dimension.toLowerCase() as 'wide' | 'long',
    encoding_format,
    compression_format: 'zip',
    statuses: ['complete']
  }
}

/**
 * POSTs an artifacts download request (single-survey or combined) and returns
 * the signed file URL. Shared by the single-survey hook and getCombinedRawData
 * so both build the payload/config the same way and both guard against a
 * response that omits primary_url — otherwise an undefined URL would flow into
 * downloadFile and silently "succeed" with an empty download.
 */
const postArtifactsDownload: (
  endpointUrl: string,
  dataFormat: DataFormat
) => Promise<string> = async (endpointUrl, dataFormat) => {
  const payload = mapDataFormatToArtifactsPayload(dataFormat)

  const response = await axios.post<{ primary_url?: string }>(
    endpointUrl,
    payload,
    createApiConfig()
  )

  const primaryUrl = response.data.primary_url
  if (!primaryUrl) {
    throw new Error('Artifacts download returned no file URL')
  }
  return primaryUrl
}

const useRawDataDownload: () => Data = () => {
  const { useAnalyticsAPI } = useAnalyticsFeatureFlag()

  const getRawData: (
    surveyId: string,
    dataFormat: DataFormat
  ) => Promise<string> = async (surveyId, dataFormat) => {
    // Wait for feature flag to be determined before making any requests
    if (useAnalyticsAPI === null) {
      return Promise.reject(
        new Error('Feature flag is still loading, please try again')
      )
    }

    if (useAnalyticsAPI) {
      // New artifacts endpoint
      const artifactsUrl = buildRequestUrl(
        `${ARTIFACTS_ENDPOINT}/${surveyId}/download`,
        process.env.REACT_APP_REST_DOMAIN
      )
      return postArtifactsDownload(artifactsUrl, dataFormat)
    } else {
      // Legacy endpoint
      const dimension = getDimensionFromDataFormat(dataFormat)
      const format = getFormatFromDataFormat(dataFormat)
      const rawDataRequestObject = {
        survey_id: surveyId,
        dimension,
        fileformat: format
      }
      const userEmail = UserSession.getEmail() ?? ''

      const res = await axios.post(
        RAW_DATA_CLOUD_FUNCTION_URL,
        JSON.stringify(rawDataRequestObject),
        {
          withCredentials: false,
          transformRequest: [
            (data) => {
              return data
            }
          ],
          headers: {
            'X-Client-Identifier': userEmail
          }
        }
      )

      if (res.status === 200) {
        return res.data
      }

      return Promise.reject()
    }
  }

  return { getRawData }
}

export default useRawDataDownload

// The only formats the backend accepts for an all-waves export (wide CSV).
export const ALL_WAVES_DATA_FORMATS: DataFormat[] = [
  DataFormat.WIDE_ENCODED,
  DataFormat.WIDE_UNENCODED
]

/**
 * Fetches a combined (multi-market) raw-data export, pooled across a project's
 * market surveys, from fd-core-responses' artifacts API. Project-scoped sibling
 * of the hook's artifacts path (POST /artifacts/multimarket/{projectId}/download):
 * it always targets the core-responses artifacts API — there is no legacy
 * equivalent, so no useAnalyticsAPI toggle and no hook is needed — and returns
 * the signed URL of the generated file. Shares postArtifactsDownload (payload
 * mapping + primary_url extraction) with the single-survey download.
 * Trackers must pass wave_id as a query parameter (not in the format body).
 * The backend adds wave and wave_label columns using the current wave metadata.
 *
 * With { allWaves: true } (trackers only) it requests one file across every
 * launched wave instead (?all_waves=true, one row per respondent per wave, only
 * questions asked in every launched wave). The backend rejects wave_id together
 * with all_waves, and accepts only wide CSV there, so waveId is never sent and
 * any other format throws before a request is made.
 */
export const getCombinedRawData: (
  projectId: string,
  dataFormat: DataFormat,
  waveId?: string,
  options?: { allWaves?: boolean }
) => Promise<string> = (projectId, dataFormat, waveId, options) => {
  const allWaves = options?.allWaves === true
  if (allWaves && !ALL_WAVES_DATA_FORMATS.includes(dataFormat)) {
    return Promise.reject(
      new Error(
        `The all-waves export only supports wide CSV, not ${dataFormat}`
      )
    )
  }

  let query = ''
  if (allWaves) {
    query = '?all_waves=true'
  } else if (waveId) {
    query = `?wave_id=${encodeURIComponent(waveId)}`
  }

  return postArtifactsDownload(
    buildRequestUrl(
      `${ARTIFACTS_MULTIMARKET_ENDPOINT}/${projectId}/download${query}`,
      process.env.REACT_APP_REST_DOMAIN
    ),
    dataFormat
  )
}
